import Analytics from '@withlocals/withlocals-analytics';
import Cookies from 'js-cookie';
import memoize from 'lodash.memoize';
import { DefaultCookieOpts } from 'providers/CookieConsentProvider';
import { WlWindow } from 'typings/globals/cordova';

import { Variant, getExperimentById } from './abTests';

declare const window: WlWindow;

interface Experiment {
  id: number;
  assignedVariant: number;
}

/**
 * Returns a random int between min (inclusive) and max (exclusive)
 */
function getRandom(min, max) {
  return Math.floor(Math.random() * (max - min) + min);
}

function pickVariant(variants: Variant[]) {
  const sortedVariants = variants
    .map((variant, index) => ({ ...variant, index }))
    .sort((a, b) => a.distribution - b.distribution);

  const random = getRandom(0, 100);

  let sum = 0;
  for (let i = 0; i < sortedVariants.length; i++) {
    sum += sortedVariants[i].distribution;
    if (random < sum) {
      return sortedVariants[i].index;
    }
  }
  return 0;
}

function track(experiments) {
  const joinedExperiments = experiments
    .map((experiment) => `t${experiment.id}v${experiment.assignedVariant}`)
    .join(';');
  Analytics.track('AB Tests', 'Experiments', joinedExperiments);
  Analytics.track({ abtest: joinedExperiments });
}

function getExperimentsFromLocalStorage(): Experiment[] {
  try {
    const abExperiments = window.localStorage.getItem('ab-experiments');
    if (!abExperiments) {
      throw new Error('No experiments in localStorage');
    }
    return JSON.parse(abExperiments) || [];
  } catch (e) {
    console.log('Error while parsing experiments', e);
    return [];
  }
}

export function getAssignedExperiments() {
  const experiments = getExperimentsFromLocalStorage();
  return experiments
    .map((experiment) => `t${experiment.id}v${experiment.assignedVariant}`)
    .join(';');
}

function storeExperiment(
  experiments: Experiment[],
  experiment: Experiment,
): Experiment[] {
  const newExperimentsList = [
    ...experiments.filter((e) => e.id !== experiment.id),
    experiment,
  ];
  try {
    window.localStorage.setItem(
      'ab-experiments',
      JSON.stringify(newExperimentsList),
    );
  } catch (e) {}
  return newExperimentsList;
}

/**
 * Get assigned variant of experiment.
 * Saves assigned variant in localStorage.
 * Tracks assigned variant to GA
 * @param {Number} experimentId
 * @param {Number} variants, amount of variants
 * @returns {Number} if no experiment was given return 0
 */
export function getAssignedVariant(experimentId: number): number {
  if (!experimentId || window.Cypress) {
    return 0;
  }

  const experiment = getExperimentById(experimentId);

  if (!experiment || !experiment.enabled || experiment.variants.length === 0) {
    return 0;
  }

  const experimentsFromLocalStorage = getExperimentsFromLocalStorage();
  const experimentFromLocalStorage = experimentsFromLocalStorage.find(
    (experiment) => experiment.id === experimentId,
  );

  if (experimentFromLocalStorage) {
    track(experimentsFromLocalStorage);
    return experimentFromLocalStorage.assignedVariant;
  } else {
    // user is not assigned to any variant yet, assign one
    const experimentForLocalStorage = {
      id: experimentId,
      assignedVariant: pickVariant(experiment.variants),
    };
    const storedExperiment = storeExperiment(
      experimentsFromLocalStorage,
      experimentForLocalStorage,
    );
    track(storedExperiment);
    return experimentForLocalStorage.assignedVariant;
  }
}

export function setAssignedVariant(experimentId: number, variant: number) {
  const experiments = getExperimentsFromLocalStorage();
  const experiment = getExperimentById(experimentId);

  if (!experiment) {
    return null;
  }

  const newExperiment = {
    id: experimentId,
    assignedVariant: variant,
  };

  return storeExperiment(experiments, newExperiment);
}

export function reqWithABTest(method, experimentId) {
  const experiment = getExperimentById(experimentId);
  if (!experiment || !experiment.cookieName) {
    throw new Error(`Please specify cookieName for test ${experimentId}.`);
  }
  const assignedVariant = getExperimentsFromLocalStorage().find(
    (experiment) => experiment.id === experimentId,
  )?.assignedVariant;

  return (data?: any) => {
    let variantLabel;
    if (
      experiment.enabled &&
      assignedVariant !== undefined &&
      experiment.cookieName
    ) {
      const variantLabel = experiment.cookieVariants
        ? experiment.cookieVariants[assignedVariant]
        : assignedVariant;

      Cookies.set(
        experiment.cookieName,
        variantLabel.toString(),
        DefaultCookieOpts,
      );
    }

    return method(data, null, (response) => {
      const variant = Object.keys(response.headers).reduce(
        (result, headerName) => {
          if (headerName.toLowerCase() === 'set-cookie') {
            const [key, value] = response.headers[headerName].split('=');
            if (key === experiment.cookieName) {
              return value;
            }
          }
          return result;
        },
        null,
      );

      if (variant) {
        if (variant !== variantLabel) {
          setAssignedVariant(
            experimentId,
            experiment.cookieVariants
              ? experiment.cookieVariants.indexOf(variant)
              : (variant as any),
          );
        }
      }
      getAssignedVariant(experimentId);

      return response;
    });
  };
}

export default memoize(getAssignedVariant);
